import React, { useState, useEffect, useContext } from 'react';
import { ChatBubbleOvalLeftEllipsisIcon } from '@heroicons/react/24/outline';
import { KeycloakContext } from '../App';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { formatDateTime } from '../lib/format';

interface UserToken {
  id: number;
  user_id: string;
  token: string;
  credits: number;
  created_at: string;
  updated_at: string;
}

interface Message {
  id: number;
  user_id: string;
  address: string;
  text: string;
  status: 'sent' | 'failed' | 'sending';
  error?: string;
  created_at: string;
}

type CodeLang = 'curl' | 'python' | 'javascript';

// WhatsApp credits are priced at half the SMS rate per product spec.
const CREDIT_PRICE_HT = 0.01;
const TVA = 0.20;

// Accepts international phone numbers in E.164 (+33612345678) or French local (06/07).
const isPhoneNumber = (value: string): boolean => {
  const v = value.trim();
  if (!v) return true;
  const cleaned = v.replace(/[\s.\-()]/g, '');
  if (/^\+\d{8,15}$/.test(cleaned)) return true;
  if (/^0[67]\d{8}$/.test(cleaned)) return true;
  return false;
};

export default function WhatsApp() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const [userToken, setUserToken] = useState<UserToken | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const [addresses, setAddresses] = useState<string[]>(['']);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const [codeLang, setCodeLang] = useState<CodeLang>('curl');

  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [buyCredits, setBuyCredits] = useState('100');
  const [buyBudget, setBuyBudget] = useState((100 * CREDIT_PRICE_HT).toFixed(2));
  const [buyingCredits, setBuyingCredits] = useState(false);

  const handleCreditsChange = (val: string) => {
    setBuyCredits(val);
    const n = parseFloat(val);
    if (!isNaN(n) && n > 0) setBuyBudget((n * CREDIT_PRICE_HT).toFixed(2));
  };
  const handleBudgetChange = (val: string) => {
    setBuyBudget(val);
    const n = parseFloat(val);
    if (!isNaN(n) && n > 0) setBuyCredits(String(Math.round(n / CREDIT_PRICE_HT)));
  };
  const buyPriceHT = parseFloat(buyBudget) || 0;
  const buyPriceTTC = Math.round(buyPriceHT * (1 + TVA) * 100) / 100;
  const buyCreditCount = parseInt(buyCredits) || 0;
  const formatPrice = (value: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(value);

  const handleBuyCredits = async () => {
    if (buyCreditCount <= 0 || buyPriceHT <= 0) {
      toast('Please enter a valid number of credits or budget', 'error');
      return;
    }
    setBuyingCredits(true);
    try {
      const res = await fetch(`${API_URL}/orders/whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${keycloak?.token}` },
        body: JSON.stringify({ credits: buyCreditCount, price_ht: buyPriceHT }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create order');
      setShowBuyCredits(false);
      setBuyCredits('100');
      setBuyBudget((100 * CREDIT_PRICE_HT).toFixed(2));
      toast('Credit order created! An administrator will fulfill it shortly.', 'success');
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setBuyingCredits(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tokenRes, messagesRes] = await Promise.all([
        fetch(`${API_URL}/whatsapp/token`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
        fetch(`${API_URL}/whatsapp/messages`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      ]);
      if (!tokenRes.ok) throw new Error('Failed to fetch WhatsApp account');
      if (!messagesRes.ok) throw new Error('Failed to fetch message history');
      setUserToken(await tokenRes.json());
      setMessages((await messagesRes.json()) || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateToken = async () => {
    const ok = await confirm({
      title: 'Regenerate API token',
      message: 'Are you sure? Your current token will stop working immediately. Any code using the old token will need to be updated.',
      confirmLabel: 'Regenerate',
      variant: 'danger',
    });
    if (!ok) return;
    setRegenerating(true);
    try {
      const res = await fetch(`${API_URL}/whatsapp/token/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!res.ok) throw new Error('Failed to regenerate token');
      const data = await res.json();
      setUserToken(data);
      setShowToken(true);
      toast('API token regenerated successfully', 'success');
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopyToken = () => {
    if (userToken?.token) {
      navigator.clipboard.writeText(userToken.token);
      toast('Token copied to clipboard', 'success');
    }
  };

  const addRecipient = () => setAddresses([...addresses, '']);
  const removeRecipient = (i: number) => setAddresses(addresses.filter((_, idx) => idx !== i));
  const updateRecipient = (i: number, val: string) => {
    const updated = [...addresses];
    updated[i] = val;
    setAddresses(updated);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const validAddresses = addresses.map((a) => a.trim()).filter(Boolean);
    if (validAddresses.length === 0) {
      toast('Please enter at least one recipient phone number', 'error');
      return;
    }
    const invalidAddresses = validAddresses.filter((a) => !isPhoneNumber(a));
    if (invalidAddresses.length > 0) {
      toast(`Invalid number(s): ${invalidAddresses.join(', ')} — use E.164 (+33612345678) or French local (06/07).`, 'error');
      return;
    }
    if (!text.trim()) {
      toast('Please enter a message', 'error');
      return;
    }
    const creditsNeeded = validAddresses.length;
    if (userToken && userToken.credits < creditsNeeded) {
      toast(`Insufficient credits. You need ${creditsNeeded} credit(s) but only have ${userToken.credits}.`, 'error');
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/whatsapp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${keycloak?.token}` },
        body: JSON.stringify({ addresses: validAddresses, text: text.trim() }),
      });
      const data = await res.json();
      if (res.status === 402) {
        toast(data.error || 'Insufficient credits', 'error');
        return;
      }
      if (!res.ok) {
        toast(data.error || 'Failed to send message', 'error');
        return;
      }
      const sentCount = (data.sent || []).length;
      const failedCount = (data.failed || []).length;
      if (sentCount > 0 && failedCount === 0) {
        toast(`${sentCount} WhatsApp message(s) sent successfully!`, 'success');
      } else if (sentCount > 0 && failedCount > 0) {
        toast(`${sentCount} sent, ${failedCount} failed.`, 'warning' as any);
      } else {
        toast('Message(s) failed to send', 'error');
      }
      setText('');
      setAddresses(['']);
      await fetchData();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      sent: 'bg-success/10 text-success ring-success/20',
      failed: 'bg-destructive/10 text-destructive ring-destructive/20',
      sending: 'bg-warning/10 text-warning ring-warning/20',
    };
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${styles[status] || styles.sending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const apiUrl = (typeof window !== 'undefined' && (window as any).APP_CONFIG?.API_URL) || API_URL;
  const token = userToken?.token || '<your-api-token>';

  const codeExamples: Record<CodeLang, string> = {
    curl: `curl -X POST "${apiUrl}/wa/send" \\
  -H "X-Api-Token: ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "address": "+33612345678",
    "text": "Hello from WhatsApp!"
  }'

# Send to multiple recipients:
curl -X POST "${apiUrl}/wa/send" \\
  -H "X-Api-Token: ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "addresses": ["+33612345678", "+33712345678"],
    "text": "Hello everyone!"
  }'`,

    python: `import requests

API_URL = "${apiUrl}"
API_TOKEN = "${token}"

def send_whatsapp(address: str, text: str) -> dict:
    response = requests.post(
        f"{API_URL}/wa/send",
        headers={"X-Api-Token": API_TOKEN, "Content-Type": "application/json"},
        json={"address": address, "text": text},
    )
    response.raise_for_status()
    return response.json()

def send_bulk(addresses: list, text: str) -> dict:
    response = requests.post(
        f"{API_URL}/wa/send",
        headers={"X-Api-Token": API_TOKEN, "Content-Type": "application/json"},
        json={"addresses": addresses, "text": text},
    )
    response.raise_for_status()
    return response.json()

# Single recipient
send_whatsapp("+33612345678", "Hello from Python!")

# Multiple recipients
send_bulk(["+33612345678", "+33712345678"], "Bulk message!")`,

    javascript: `const API_URL = "${apiUrl}";
const API_TOKEN = "${token}";

async function sendWhatsApp(address, text) {
  const response = await fetch(\`\${API_URL}/wa/send\`, {
    method: "POST",
    headers: {
      "X-Api-Token": API_TOKEN,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ address, text }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || "Failed to send message");
  }
  return response.json();
}

async function sendBulk(addresses, text) {
  const response = await fetch(\`\${API_URL}/wa/send\`, {
    method: "POST",
    headers: {
      "X-Api-Token": API_TOKEN,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ addresses, text }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || "Failed to send messages");
  }
  return response.json();
}

// Single recipient
sendWhatsApp("+33612345678", "Hello from JavaScript!")
  .then(res => console.log(res))
  .catch(err => console.error(err));

// Multiple recipients
sendBulk(["+33612345678", "+33712345678"], "Bulk message!")
  .then(res => console.log(res))
  .catch(err => console.error(err));`,
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <PageHeader
          icon={<ChatBubbleOvalLeftEllipsisIcon />}
          title="WhatsApp"
          description="Send messages on WhatsApp using the API."
          actions={
            <button type="button" className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm" disabled>
              Order Credits
            </button>
          }
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-card shadow rounded-lg p-6 space-y-3 border border-border">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
          <div className="bg-card shadow rounded-lg p-6 space-y-3 border border-border">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4">
        <p className="text-sm text-destructive">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        icon={<ChatBubbleOvalLeftEllipsisIcon />}
        title="WhatsApp"
        description="Send messages on WhatsApp using the API."
        actions={
          <button
            type="button"
            onClick={() => setShowBuyCredits(true)}
            className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90"
          >
            Order Credits
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Send message form */}
        <div className="bg-card shadow rounded-lg">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-base font-semibold text-foreground">Send a WhatsApp Message</h2>
            <p className="mt-1 text-sm text-muted-foreground">1 credit per recipient</p>
          </div>
          <form onSubmit={handleSend} className="p-6 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-foreground">Recipients</label>
                <button type="button" onClick={addRecipient} className="text-xs text-primary hover:text-primary/80 font-medium">
                  + Add recipient
                </button>
              </div>
              <div className="space-y-2">
                {addresses.map((addr, i) => (
                  <div key={i} className="flex flex-col gap-1">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={addr}
                        onChange={(e) => updateRecipient(i, e.target.value)}
                        placeholder="+33612345678"
                        className={`flex-1 rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus-visible:ring-ring focus:border-primary ${
                          addr.trim() && !isPhoneNumber(addr) ? 'border-red-400 bg-destructive/10' : 'border-border'
                        }`}
                      />
                      {addresses.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRecipient(i)}
                          className="text-muted-foreground hover:text-red-500 p-2"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {addr.trim() && !isPhoneNumber(addr) && (
                      <p className="text-xs text-red-500 pl-1">Use E.164 (+33612345678) or French local (06/07).</p>
                    )}
                  </div>
                ))}
              </div>
              {addresses.filter((a) => a.trim()).length > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Cost: {addresses.filter((a) => a.trim() && isPhoneNumber(a)).length} credit(s)
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Message</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder="Type your WhatsApp message..."
                required
                className="w-full rounded-md border border-border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus-visible:ring-ring focus:border-primary resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={sending || !text.trim() || addresses.filter((a) => a.trim()).length === 0}
              className="w-full inline-flex justify-center items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send Message
                </>
              )}
            </button>

            {userToken && userToken.credits === 0 && (
              <p className="text-xs text-amber-600 text-center">
                You have no credits.{' '}
                <button type="button" onClick={() => setShowBuyCredits(true)} className="underline font-medium">
                  Order Credits
                </button>{' '}
                to send messages.
              </p>
            )}
          </form>
        </div>

        {/* API Token card */}
        <div className="bg-card shadow rounded-lg">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-base font-semibold text-foreground">Your API Token</h2>
            <p className="mt-1 text-sm text-muted-foreground">Use this token to send WhatsApp messages from your code</p>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Token</label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type={showToken ? 'text' : 'password'}
                    readOnly
                    value={userToken?.token || ''}
                    className="w-full rounded-md border border-border px-3 py-2 text-sm font-mono bg-muted/30 focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="px-3 py-2 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted/40 text-xs"
                  title={showToken ? 'Hide token' : 'Show token'}
                >
                  {showToken ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCopyToken}
                  className="px-3 py-2 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted/40 text-xs"
                  title="Copy token"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Keep this token secret — anyone with it can use your credits.</p>
            </div>

            <button
              type="button"
              onClick={handleRegenerateToken}
              disabled={regenerating}
              className="w-full inline-flex justify-center items-center rounded-md border border-destructive/40 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {regenerating ? 'Regenerating...' : 'Regenerate Token'}
            </button>

            <div className="pt-2">
              <p className="text-xs font-medium text-muted-foreground mb-1">CREDITS</p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-foreground">{userToken?.credits ?? 0}</span>
                <button
                  type="button"
                  onClick={() => setShowBuyCredits(true)}
                  className="text-sm text-primary font-medium hover:text-primary/80"
                >
                  Order Credits →
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{formatPrice(CREDIT_PRICE_HT)} / credit excl. VAT</p>
            </div>
          </div>
        </div>
      </div>

      {/* API Usage examples */}
      <div className="bg-card shadow rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">API Usage Examples</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Use <code className="bg-muted px-1 py-0.5 rounded text-xs">X-Api-Token</code> header to authenticate
          </p>
        </div>
        <div className="p-6">
          <div className="flex gap-2 mb-4">
            {(['curl', 'python', 'javascript'] as CodeLang[]).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setCodeLang(lang)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  codeLang === lang ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted'
                }`}
              >
                {lang === 'javascript' ? 'JavaScript' : lang.charAt(0).toUpperCase() + lang.slice(1)}
              </button>
            ))}
          </div>
          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm leading-relaxed">
            <code>{codeExamples[codeLang]}</code>
          </pre>
          <p className="mt-3 text-xs text-muted-foreground">
            <strong>Note:</strong> Each message sent to a recipient deducts 1 credit from your balance. Recipients must be phone numbers in E.164 format (e.g.{' '}
            <code className="bg-muted px-1 rounded">+33612345678</code>).
          </p>
        </div>
      </div>

      {/* Message history */}
      <div className="bg-card shadow rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">Message History</h2>
          <p className="mt-1 text-sm text-muted-foreground">{messages.length} message(s) sent in total</p>
        </div>
        {messages.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <p className="mt-2 text-sm text-muted-foreground">No messages sent yet. Send your first WhatsApp message above!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Recipient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Message</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {messages.map((msg) => (
                  <tr key={msg.id} className="hover:bg-muted/40">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(msg.status)}
                      {msg.status === 'failed' && msg.error && (
                        <p className="text-xs text-red-500 mt-1 max-w-xs truncate" title={msg.error}>{msg.error}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground font-mono">{msg.address}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground max-w-xs">
                      <p className="truncate" title={msg.text}>{msg.text}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatDateTime(msg.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showBuyCredits && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm transition-opacity" onClick={() => setShowBuyCredits(false)} />
            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-card px-6 pt-5 pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Order WhatsApp Credits</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{formatPrice(CREDIT_PRICE_HT)} per credit excl. VAT · 20% VAT applies</p>
                  </div>
                  <button type="button" onClick={() => setShowBuyCredits(false)} className="text-muted-foreground hover:text-muted-foreground">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Number of credits</label>
                    <input
                      type="number"
                      min="1"
                      value={buyCredits}
                      onChange={(e) => handleCreditsChange(e.target.value)}
                      className="w-full rounded-md border border-border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus-visible:ring-ring focus:border-primary"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">1 credit = 1 message to 1 recipient</p>
                  </div>
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-border" />
                    <span className="mx-3 text-xs text-muted-foreground">or enter budget</span>
                    <div className="flex-1 border-t border-border" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Budget (excl. VAT)</label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={buyBudget}
                        onChange={(e) => handleBudgetChange(e.target.value)}
                        className="w-full rounded-md border border-border px-3 py-2 pr-8 text-sm shadow-sm focus:outline-none focus:ring-2 focus-visible:ring-ring focus:border-primary"
                      />
                      <span className="absolute right-3 top-2 text-sm text-muted-foreground">€</span>
                    </div>
                  </div>
                  <div className="rounded-lg bg-muted/30 border border-border p-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{buyCreditCount} credits × {formatPrice(CREDIT_PRICE_HT)}</span>
                      <span className="text-foreground font-medium">{formatPrice(buyPriceHT)}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-muted-foreground">VAT (20%)</span>
                      <span className="text-foreground">{formatPrice(buyPriceTTC - buyPriceHT)}</span>
                    </div>
                    <div className="flex justify-between mt-2 pt-2 border-t border-border">
                      <span className="font-semibold text-foreground">Total incl. VAT</span>
                      <span className="font-semibold text-foreground">{formatPrice(buyPriceTTC)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-muted/30 px-6 py-3 flex flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={handleBuyCredits}
                  disabled={buyingCredits || buyCreditCount <= 0}
                  className="inline-flex justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {buyingCredits ? 'Creating order...' : `Order ${buyCreditCount} credits`}
                </button>
                <button type="button" onClick={() => setShowBuyCredits(false)} className="inline-flex justify-center rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground bg-card hover:bg-muted/40">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
